/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Autocomplete from "@mui/material/Autocomplete";
import FileSaver from "file-saver";
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_HOHOLOK, HOHOLOK_UPLOADS } from "utils";

// Helper format tanggal Indonesia
function formatTanggalIndo(dateStr) {
  if (!dateStr) return "-";
  const bulan = [
    "Januari",
    "Februari",
    "Maret",
    "April",
    "Mei",
    "Juni",
    "Juli",
    "Agustus",
    "September",
    "Oktober",
    "November",
    "Desember",
  ];
  const d = new Date(dateStr);
  if (isNaN(d)) return "-";
  return `${d.getDate()} ${bulan[d.getMonth()]} ${d.getFullYear()}`;
}

function UsersList() {
  const [menu, setMenu] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState({
    nama_user: "",
    username: "",
    email: "",
    id_perusahaan: "",
    uid_RSS: "",
    profil: "",
  });
  const [editId, setEditId] = useState(null);
  const [filterPerusahaan, setFilterPerusahaan] = useState("All");
  const [search, setSearch] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteNama, setDeleteNama] = useState("");
  const navigate = useNavigate();

  // Ambil data user
  const fetchUsers = () => {
    setLoading(true);
    axios
      .get(`${API_HOHOLOK}users`)
      .then((res) => {
        setUsers(res.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };
  useEffect(() => {
    fetchUsers();
  }, []);

  // Opsi jenis perusahaan dari data user (berdasarkan jenis_perusahaan unik)
  const jenisOptions = [
    ...new Set(users.map((u) => u.jenis_perusahaan).filter(Boolean)),
  ];

  // Data baris, filter dan search
  const filteredRows = users
    .filter((item) =>
      filterPerusahaan === "All"
        ? true
        : item.jenis_perusahaan === filterPerusahaan
    )
    .filter((item) =>
      item.nama_user?.toLowerCase().includes(search.trim().toLowerCase())
    )
    .map((item) => ({
      ...item,
    }));

  // Kolom tabel
  const columns = [
    {
      Header: "No",
      accessor: "no",
      Cell: ({ row }) => row.index + 1,
      width: 60,
    },
    { Header: "Nama User", accessor: "nama_user" },
    { Header: "Username", accessor: "username" },
    { Header: "Email", accessor: "email" },
    { Header: "Nama Perusahaan", accessor: "nama_perusahaan" },
    { Header: "Email Perusahaan", accessor: "email_perusahaan" },
    { Header: "Alamat Perusahaan", accessor: "alamat_perusahaan" },
    { Header: "Jenis Perusahaan", accessor: "jenis_perusahaan" },
    {
      Header: "Profil",
      accessor: "profil",
      Cell: ({ value }) =>
        value ? (
          <a
            href={HOHOLOK_UPLOADS + value}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={HOHOLOK_UPLOADS + value}
              alt="profil"
              style={{ width: 32, height: 32, borderRadius: "50%" }}
            />
          </a>
        ) : (
          "-"
        ),
    },
    {
      Header: "CreatedAt",
      accessor: "createdAt",
      Cell: ({ value }) => formatTanggalIndo(value),
    },
    {
      Header: "Aksi",
      accessor: "aksi",
      Cell: ({ row }) => (
        <>
          <IconButton
            color="info"
            onClick={() => handleEdit(row.original)}
            size="small"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => handleOpenDelete(row.original)}
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const handleEdit = (data) => {
    setForm({
      nama_user: data.nama_user || "",
      username: data.username || "",
      email: data.email || "",
      id_perusahaan: data.id_perusahaan || "",
      uid_RSS: data.uid_RSS || "",
      profil: data.profil || "",
    });
    setIsEdit(true);
    setEditId(data.id_user);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setIsEdit(false);
    setEditId(null);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      if (isEdit && editId) {
        await axios.put(`${API_HOHOLOK}users/${editId}`, form);
      }
      handleCloseDialog();
      fetchUsers();
    } catch (err) {
      alert("Gagal menyimpan data user");
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    if (!filteredRows.length) return;
    const header = [
      "No",
      "Nama User",
      "Username",
      "Email",
      "Nama Perusahaan",
      "Email Perusahaan",
      "Alamat Perusahaan",
      "Jenis Perusahaan",
      "Profil",
      "CreatedAt",
    ];
    const csvRows = [
      header.join(","),
      ...filteredRows.map((row, index) =>
        [
          index + 1,
          row.nama_user,
          row.username,
          row.email,
          row.nama_perusahaan,
          row.email_perusahaan,
          row.alamat_perusahaan,
          row.jenis_perusahaan,
          row.profil,
          formatTanggalIndo(row.createdAt),
        ]
          .map((v) => `"${(v || "").replace(/"/g, '""')}"`)
          .join(",")
      ),
    ];
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    FileSaver.saveAs(blob, "users.csv");
  };

  const openMenu = (event) => setMenu(event.currentTarget);
  const closeMenu = () => setMenu(null);

  const renderMenu = (
    <Menu
      anchorEl={menu}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={Boolean(menu)}
      onClose={closeMenu}
      keepMounted
    >
      <MenuItem
        selected={filterPerusahaan === "All"}
        onClick={() => {
          setFilterPerusahaan("All");
          closeMenu();
        }}
      >
        Semua Jenis Perusahaan
      </MenuItem>
      {jenisOptions.map((jenis) => (
        <MenuItem
          key={jenis}
          selected={filterPerusahaan === jenis}
          onClick={() => {
            setFilterPerusahaan(jenis);
            closeMenu();
          }}
        >
          {jenis}
        </MenuItem>
      ))}
    </Menu>
  );

  // Handler buka dialog hapus
  const handleOpenDelete = (data) => {
    setDeleteId(data.id_user);
    setDeleteNama(data.nama_user);
    setOpenDeleteDialog(true);
  };
  // Handler tutup dialog hapus
  const handleCloseDelete = () => {
    setOpenDeleteDialog(false);
    setDeleteId(null);
    setDeleteNama("");
  };
  // Handler hapus user
  const handleDeleteUser = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API_HOHOLOK}users/${deleteId}`);
      handleCloseDelete();
      fetchUsers();
    } catch (err) {
      alert("Gagal menghapus user");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => {
              navigate("/team/users/new-user");
            }}
          >
            Tambahkan user baru
          </MDButton>
          <MDBox display="flex">
            <MDButton
              variant={menu ? "contained" : "outlined"}
              color="dark"
              onClick={openMenu}
            >
              Filter Perusahaan&nbsp;
              <Icon>keyboard_arrow_down</Icon>
            </MDButton>
            {renderMenu}
            <MDBox ml={1}>
              <MDButton
                variant="outlined"
                color="dark"
                onClick={handleExportCSV}
              >
                <Icon>description</Icon>
                &nbsp;Export CSV
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
        <Card>
          <MDBox p={2} pb={0} display="flex" alignItems="center" gap={2}>
            <TextField
              label="Cari Nama User"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 250 }}
            />
          </MDBox>
          <div style={{ overflow: "auto", maxHeight: "70vh" }}>
            <DataTable
              table={{ columns, rows: filteredRows }}
              entriesPerPage={false}
              canSearch
              isLoading={loading}
              noEndBorder={false}
              showTotalEntries={false}
            />
          </div>
        </Card>
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit User</DialogTitle>
          <DialogContent dividers>
            <TextField
              margin="dense"
              label="Nama User"
              name="nama_user"
              value={form.nama_user}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              margin="dense"
              label="Username"
              name="username"
              value={form.username}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              margin="dense"
              label="Email"
              name="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              required
            />
            <Autocomplete
              options={jenisOptions}
              value={form.jenis_perusahaan || ""}
              onChange={(e, newValue) =>
                setForm({ ...form, jenis_perusahaan: newValue || "" })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Jenis Perusahaan"
                  margin="dense"
                  required
                />
              )}
              fullWidth
              disableClearable
            />
            <TextField
              margin="dense"
              label="UID RSS"
              name="uid_RSS"
              value={form.uid_RSS}
              onChange={handleChange}
              fullWidth
            />
            {form.profil && (
              <MDBox mt={2} mb={1}>
                <img
                  src={HOHOLOK_UPLOADS + form.profil}
                  alt="profil"
                  style={{ width: 64, height: 64, borderRadius: "50%" }}
                />
              </MDBox>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseDialog}>
              Batal
            </MDButton>
            <MDButton color="info" variant="contained" onClick={handleSubmit}>
              Simpan
            </MDButton>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openDeleteDialog}
          onClose={handleCloseDelete}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Konfirmasi Hapus User</DialogTitle>
          <DialogContent dividers>
            <MDTypography variant="body1">
              Apakah Anda yakin ingin menghapus user <b>{deleteNama}</b>?
            </MDTypography>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseDelete}>
              Batal
            </MDButton>
            <MDButton
              color="error"
              variant="contained"
              onClick={handleDeleteUser}
            >
              Hapus
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default UsersList;
